import React from "react";
import { Check } from "lucide-react";
import { LearnHeader, Markdown, PromptBox, Sources, PrevNext, epCode, absUrl, HANDLE } from "@/components/learn/common";
import { useLearnItem, LearnFrame, LearnFooter } from "@/pages/learn/LearnFrame";
import { useHead } from "@/lib/useHead";

function Video({ item }) {
  if (item.youtube_id) {
    return (
      <div className="mx-auto aspect-[9/16] w-full max-w-[340px] overflow-hidden rounded-[10px] border border-border bg-black" data-testid="learn-video-embed">
        <iframe src={`https://www.youtube-nocookie.com/embed/${item.youtube_id}`} title={item.title} className="h-full w-full" loading="lazy"
          allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share" allowFullScreen />
      </div>
    );
  }
  return (
    <div className="relative mx-auto aspect-[9/16] w-full max-w-[340px] overflow-hidden rounded-[10px] border border-border bg-[#F1EDE4]" data-testid="learn-video-placeholder">
      {item.cover_url && <img src={item.cover_url} alt={item.title} className="h-full w-full object-cover" />}
      <span className="absolute bottom-3 left-3 rounded-md bg-ink/85 px-3 py-1.5 text-xs font-semibold text-white">Video coming soon</span>
    </div>
  );
}

function Episode({ data }) {
  const { item, previous, next } = data;
  const url = absUrl(item.path);
  const cover = absUrl(item.cover_url);
  useHead({
    title: `${epCode(item.ep)} ${item.title} — ${HANDLE}`.trim(), description: item.summary || item.title, image: cover, url, type: "article",
    jsonLd: [
      { "@context": "https://schema.org", "@type": "VideoObject", name: item.title, description: item.summary || item.title,
        thumbnailUrl: cover ? [cover] : undefined, uploadDate: item.publish_at,
        embedUrl: item.youtube_id ? `https://www.youtube.com/embed/${item.youtube_id}` : undefined,
        contentUrl: item.youtube_id ? `https://www.youtube.com/shorts/${item.youtube_id}` : undefined },
      { "@context": "https://schema.org", "@type": "Article", headline: item.title, description: item.summary || undefined, image: cover ? [cover] : undefined,
        datePublished: item.publish_at, dateModified: item.updated_at || item.publish_at, mainEntityOfPage: url,
        author: { "@type": "Person", name: "Shubham Bapna" }, publisher: { "@type": "Organization", name: "Bapna Digital" } },
    ],
  });
  return (
    <article className="mx-auto max-w-[760px] space-y-10 px-5 py-12" data-testid="learn-episode-page">
      <LearnHeader item={item} kicker={epCode(item.ep) || "Episode"} />
      <Video item={item} />
      {item.learn?.length > 0 && (
        <section className="rounded-[10px] border border-border bg-card p-5" data-testid="learn-what-youll-learn">
          <h2 className="font-heading text-lg font-bold text-ink">What you'll learn</h2>
          <ul className="mt-3 space-y-2">
            {item.learn.map((l, i) => <li key={i} className="flex gap-2.5 text-[15px] text-ink"><Check className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent" />{l}</li>)}
          </ul>
        </section>
      )}
      {item.body_md && <Markdown testid="learn-body">{item.body_md}</Markdown>}
      <PromptBox prompt={item.prompt} />
      <Sources sources={item.sources} />
      <LearnFooter item={item} url={url} />
      <PrevNext previous={previous} next={next} prefix="ep" label="episode" />
    </article>
  );
}

export default function LearnEpisode() {
  const q = useLearnItem("episode");
  return <LearnFrame query={q}>{(data) => <Episode data={data} />}</LearnFrame>;
}
